import React, { useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import countryList from "react-select-country-list";
import CreateNewPlanPage from "../../../entities/SuperAdminPage/CreateNewPlan";
import { planService } from "../../../api";
import { useAlert } from "../../../context/AlertContext";
import LoaderHelper from "../../../utils/Loader/LoaderHelper";
import { planModuleDefaultToggles, planModuleDefaultValues } from "../../../constants/data";

const toStringValue = (value) => (value === null || value === undefined ? "" : String(value));

const getValue = (obj, key, fallback) => {
  if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key];
  return fallback;
};

const toSafeNumber = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const clampCompaniesCount = (value) => {
  const numeric = toSafeNumber(value);
  if (numeric <= 0) return 0;
  if (numeric > 1000) return 1000;
  return numeric;
};

const mapExportFormatToPayload = (value) => (value === "Excel" ? "XLSX" : value);

const normalizeExportFormatsForPayload = (value) => {
  if (Array.isArray(value)) return value.map(mapExportFormatToPayload);
  if (!value) return [];
  return [mapExportFormatToPayload(value)];
};

const normalizeExportFormatsForUI = (value) => {
  if (Array.isArray(value)) {
    return value.map((v) => (v === "XLSX" ? "Excel" : v));
  }
  if (typeof value === "string" && value) {
    return [value === "XLSX" ? "Excel" : value];
  }
  return [];
};

const countryLabelMap = countryList().getData().reduce((acc, option) => {
  const normalizedKey = String(option?.label || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

  if (normalizedKey) {
    acc[normalizedKey] = option.label;
  }

  return acc;
}, {});

const normalizeCountryForForm = (country) => {
  const trimmedCountry = String(country || "").trim();
  if (!trimmedCountry) return "";

  const normalizedKey = trimmedCountry.toLowerCase().replace(/\s+/g, "");
  return countryLabelMap[normalizedKey] || trimmedCountry;
};

const CreateNewPlan = () => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const { showAlert } = useAlert();
  const isEditMode = !!id;

  const fetchPlanData = useCallback(async (
    setFormData,
    setInitialFormData,
    setModuleToggles,
    setModuleValues,
    setInitialModuleToggles,
    setInitialModuleValues,
    setPlanScope,
    setInitialPlanScope
  ) => {
    setFetching(true);
    LoaderHelper.loaderStatus(true);
    try {
      const result = await planService.getById(id);
      if (result && !result.error) {
        const plan = result.data || result;
        const moduleAccess = plan.moduleAccess || {};
        const moduleLimits = plan.moduleLimits || {};

        const loadedData = {
          planName: plan.planName || "",
          billingCycle: plan.billingCycle || "",
          price: toStringValue(plan.price),
          currency: plan.currency || "",
          country: normalizeCountryForForm(plan.country),
          maxEmployees: toStringValue(
            getValue(moduleLimits, "activeEmployees", getValue(plan, "activeEmployees", plan.employeeLimit))
          ),
          payslipsPerYear: toStringValue(
            getValue(moduleLimits, "payslipLimit", getValue(plan, "payslipLimit", plan.payslipsLimit))
          ),
          isActive: plan.isActive !== undefined ? plan.isActive : true,
          trialEnabled: plan.trialEnabled || false,
          trialDuration: toStringValue(plan.trialDuration),
        };

        const loadedToggles = { ...planModuleDefaultToggles };
        Object.keys(loadedToggles).forEach((key) => {
          if (typeof moduleAccess[key] === "boolean") {
            loadedToggles[key] = moduleAccess[key];
            return;
          }
          if (typeof plan[key] === "boolean") loadedToggles[key] = plan[key];
        });

        const loadedValues = {
          ...planModuleDefaultValues,
          companies: toStringValue(
            getValue(
              moduleLimits,
              "companiesPerPlan",
              getValue(moduleLimits, "activeCompanies", getValue(plan, "activeCompanies", getValue(plan, "companiesPerPlan", plan.companies)))
            )
          ),
          invoice: toStringValue(
            getValue(moduleLimits, "invoicesPerPlan", getValue(plan, "invoicesPerPlan", plan.invoice))
          ),
          operatorsPerPlan: toStringValue(
            getValue(moduleLimits, "operatorsPerPlan", plan.operatorsPerPlan)
          ),
          invoiceValueCap: toStringValue(getValue(moduleLimits, "invoiceValueCap", plan.invoiceValueCap)),
          attendanceDays: toStringValue(
            getValue(moduleLimits, "attendanceDaysPerPlan", getValue(plan, "attendanceDaysPerPlan", plan.attendanceDays))
          ),
          loans: toStringValue(getValue(moduleLimits, "activeLoans", getValue(plan, "activeLoans", plan.loans))),
          deductions: toStringValue(
            getValue(moduleLimits, "activeDeductions", getValue(plan, "activeDeductions", plan.deductions))
          ),
          leaveRequests: toStringValue(
            getValue(
              moduleLimits,
              "leaveRequestsPerPlan",
              getValue(plan, "leaveRequestsPerPlan", plan.leaveRequests)
            )
          ),
          commissionRuns: toStringValue(
            getValue(
              moduleLimits,
              "commissionRunsPerPlan",
              getValue(plan, "commissionRunsPerPlan", plan.commissionRuns)
            )
          ),
          travelClaims: toStringValue(
            getValue(
              moduleLimits,
              "travelClaimsPerPlan",
              getValue(plan, "travelClaimsPerPlan", plan.travelClaims)
            )
          ),
          allowances: toStringValue(
            getValue(
              moduleLimits,
              "allowancesPerPlan",
              getValue(plan, "allowancesPerPlan", plan.allowances)
            )
          ),
          documentsUploadPerEmployee: toStringValue(
            getValue(moduleLimits, "employeeDocsPerEmployee", getValue(moduleLimits, "documentsUploadPerEmployee", getValue(plan, "employeeDocsPerEmployee", plan.documentsUploadPerEmployee)))
          ),
          recruitment: toStringValue(
            getValue(
              moduleLimits,
              "recruitmentPerPlan",
              getValue(plan, "recruitmentPerPlan", plan.recruitment)
            )
          ),
          timesheet: toStringValue(
            getValue(moduleLimits, "timesheetPerPlan", getValue(plan, "timesheetPerPlan", plan.timesheet))
          ),
          projects: toStringValue(
            getValue(moduleLimits, "activeProjects", getValue(plan, "activeProjects", plan.projects))
          ),
          payrollRuns: toStringValue(
            getValue(moduleLimits, "payrollRunsPerPlan", getValue(plan, "payrollRunsPerPlan", plan.payrollRuns))
          ),
          reportDownloads: toStringValue(
            getValue(
              moduleLimits,
              "reportDownloadsPerPlan",
              getValue(plan, "reportDownloadsPerPlan", plan.reportDownloads)
            )
          ),
          exportFormats: normalizeExportFormatsForUI(
            getValue(moduleLimits, "exportFormats", plan.exportFormats)
          ),
        };

        const loadedPlanScope = plan.planScope || "perCompany";

        setFormData(loadedData);
        setInitialFormData(loadedData);
        if (setModuleToggles) setModuleToggles(loadedToggles);
        if (setModuleValues) setModuleValues(loadedValues);
        if (setInitialModuleToggles) setInitialModuleToggles(loadedToggles);
        if (setInitialModuleValues) setInitialModuleValues(loadedValues);
        if (setPlanScope) setPlanScope(loadedPlanScope);
        if (setInitialPlanScope) setInitialPlanScope(loadedPlanScope);
      } else {
        showAlert(result?.message || "Failed to load plan data", "error");
      }
      setFetching(false);
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      setFetching(false);
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || "Error loading plan data", "error");
    }
  }, [id, showAlert]);

  const handleCreatePlan = async (formData, moduleToggles = {}, moduleValues = {}, planScope = "perCompany", pricingList = []) => {
    setLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const safeToggles = { ...planModuleDefaultToggles, ...moduleToggles };
      const { companies: _ignoreCompaniesToggle, documentsUploadPerEmployee: _ignoreDocsToggle, ...safeTogglesWithoutCompanies } = safeToggles;

      // Dodo model: no base price. Price lives in pricing[] per country. On
      // create we send the multi-country pricing[] the SuperAdmin assembled; on
      // edit, per-country pricing is managed separately (Country Pricing section)
      // so we don't send/clobber the pricing array here.
      const pricing = Array.isArray(pricingList)
        ? pricingList.map((p) => ({
            country: String(p.country || "").trim(),
            currency: String(p.currency || "").toUpperCase(),
            price: Number(p.price),
          }))
        : [];
      // Used only for the post-save navigation state (first configured country).
      const planCountry = pricing[0]?.country || "";

      const payload = {
        planName: formData.planName,
        billingCycle: formData.billingCycle,
        ...(isEditMode ? {} : { pricing }),
        ...safeTogglesWithoutCompanies,
        companiesPerPlan: clampCompaniesCount(moduleValues.companies),
        invoicesPerPlan: toSafeNumber(moduleValues.invoice),
        invoiceValueCap: toSafeNumber(moduleValues.invoiceValueCap),
        activeEmployees: toSafeNumber(formData.maxEmployees),
        employeeDocsPerEmployee: toSafeNumber(moduleValues.documentsUploadPerEmployee),
        // Blank means unlimited attendance for the billing period — unlike every
        // other moduleLimits field, a blank value here must not be coerced to 0
        // and sent; the key is simply omitted so the backend treats it as uncapped.
        ...(String(moduleValues.attendanceDays ?? "").trim() !== ""
          ? { attendanceDaysPerPlan: toSafeNumber(moduleValues.attendanceDays) }
          : {}),
        activeLoans: toSafeNumber(moduleValues.loans),
        activeDeductions: toSafeNumber(moduleValues.deductions),
        leaveRequestsPerPlan: toSafeNumber(moduleValues.leaveRequests),
        commissionRunsPerPlan: toSafeNumber(moduleValues.commissionRuns),
        travelClaimsPerPlan: toSafeNumber(moduleValues.travelClaims),
        allowancesPerPlan: toSafeNumber(moduleValues.allowances),
        recruitmentPerPlan: toSafeNumber(moduleValues.recruitment),
        timesheetPerPlan: toSafeNumber(moduleValues.timesheet),
        activeProjects: toSafeNumber(moduleValues.projects),
        payrollRunsPerPlan: toSafeNumber(moduleValues.payrollRuns),
        reportDownloadsPerPlan: toSafeNumber(moduleValues.reportDownloads),
        payslipLimit: toSafeNumber(formData.payslipsPerYear),
        exportFormats: normalizeExportFormatsForPayload(moduleValues.exportFormats),
        isActive: formData.isActive !== undefined ? formData.isActive : true,
        planScope: planScope,
        trialEnabled: !!formData.trialEnabled,
        ...(formData.trialEnabled ? { trialDuration: Number(formData.trialDuration) } : {}),
        moduleLimits: {
          activeEmployees: toSafeNumber(formData.maxEmployees),
          ...(safeToggles.operators ? {
            operatorsPerPlan: toSafeNumber(moduleValues.operatorsPerPlan)
          } : {
            operators: false,
            operatorsPerPlan: 0
          })
        }
      };

      let result;
      if (isEditMode) {
        result = await planService.update(id, payload);
        if (result?.success !== false) {
          showAlert(result?.message || "Plan updated successfully", "success");
          navigate("/dashboard/planManagement", { state: { country: planCountry } });
        } else {
          showAlert(result?.message || "Failed to update plan", "error");
        }
      } else {
        result = await planService.create(payload);
        if (result?.success !== false) {
          showAlert(result?.message || "Plan created successfully", "success");
          navigate("/dashboard/planManagement", { state: { country: planCountry } });
        } else {
          showAlert(result?.message || "Failed to create plan", "error");
        }
      }
      setLoading(false);
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      setLoading(false);
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || `Error ${isEditMode ? "updating" : "creating"} plan`, "error");
    }
  };

  return (
    <CreateNewPlanPage
      loading={loading}
      fetching={fetching}
      handleCreatePlan={handleCreatePlan}
      fetchPlanData={fetchPlanData}
      isEditMode={isEditMode}
      planId={id}
    />
  );
};

export default CreateNewPlan;
